import React, { useEffect, useState } from 'react';
import { Box, TextField, InputAdornment, Button } from '@mui/material';
import { Search as SearchIcon, Close as ClearIcon } from '@mui/icons-material';
import { adminColors, radius, TOUCH_HIT_SX } from '../../../theme/adminTheme';
import useDebouncedValue from '../../../hooks/useDebouncedValue';

// Search + filter-controls row bound directly to a `useQueryState()` pair,
// so every admin list view wires filters the same way instead of each one
// growing its own local search-state-plus-effect. Renders a debounced
// search field, a slot for column-filter controls (SchoolPicker, role
// selects, ...), and a "Clear filters" affordance that
// only appears once something actually differs from `defaults`.
//
// queryState / setQueryState: the [state, setState] pair from
// hooks/useQueryState.js.
// defaults: the same defaults object passed to useQueryState — reused here
// to know what "no filters applied" (and therefore whether to show Clear)
// means, and what search resets to on Clear.
// resetOnSearch: query keys to reset alongside the search key whenever the
// debounced search value actually changes — defaults to `['page']`, since
// a new search should always land back on page 1.
export default function FilterBar({
  queryState,
  setQueryState,
  defaults = {},
  searchKey = 'q',
  searchPlaceholder = 'Search…',
  resetOnSearch = ['page'],
  debounceMs = 300,
  children,
  sx,
}) {
  const currentSearch = queryState[searchKey] || '';
  const [draft, setDraft] = useState(currentSearch);
  const debouncedDraft = useDebouncedValue(draft, debounceMs);

  // Keeps the field in sync with filter changes that didn't originate here
  // — a parent "Clear filters" call, browser back/forward, or a deep link
  // arriving with `?q=...` already set.
  useEffect(() => {
    setDraft(currentSearch);
    // Only the URL's value should ever reset the draft mid-typing; re-firing
    // this on every `currentSearch` render (rather than only when it
    // meaningfully changes) is intentional here since it's a cheap string
    // comparison, not a fetch.
  }, [currentSearch]);

  useEffect(() => {
    if (debouncedDraft === currentSearch) return;
    const updates = { [searchKey]: debouncedDraft };
    resetOnSearch.forEach((key) => {
      updates[key] = defaults[key];
    });
    setQueryState(updates);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only the debounced value should trigger this; re-running on currentSearch/defaults churn would fight the field's own typing state
  }, [debouncedDraft]);

  const isAtDefault = (key) => {
    const value = queryState[key];
    const defaultValue = defaults[key];
    if (Array.isArray(defaultValue)) {
      return Array.isArray(value) && value.length === defaultValue.length;
    }
    return (value ?? '') === (defaultValue ?? '');
  };

  const hasActiveFilters = Object.keys(defaults).some((key) => !isAtDefault(key));

  const handleClear = () => {
    setDraft(defaults[searchKey] || '');
    setQueryState({ ...defaults });
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25, ...sx }}>
      <TextField
        size="small"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={searchPlaceholder}
        aria-label={searchPlaceholder}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: adminColors.inkSoft }} />
            </InputAdornment>
          ),
          endAdornment: draft ? (
            <InputAdornment position="end">
              <Box
                role="button"
                tabIndex={0}
                aria-label="Clear search"
                onClick={() => setDraft('')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setDraft('');
                  }
                }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  borderRadius: radius.pill,
                  color: adminColors.inkSoft,
                  '&:hover': { color: adminColors.ink },
                  '&:focus-visible': { outline: `2px solid ${adminColors.accent}`, outlineOffset: 2 },
                  ...TOUCH_HIT_SX,
                }}
              >
                <ClearIcon sx={{ fontSize: 16 }} />
              </Box>
            </InputAdornment>
          ) : undefined,
        }}
        sx={{ minWidth: 220, flex: '0 1 320px' }}
      />
      {children}
      {hasActiveFilters && (
        <Button
          size="small"
          onClick={handleClear}
          sx={{
            ...TOUCH_HIT_SX,
            fontWeight: 600,
            color: adminColors.inkSoft,
            '&:hover': { color: adminColors.ink, backgroundColor: adminColors.surface },
          }}
        >
          Clear filters
        </Button>
      )}
    </Box>
  );
}
